/** @jsxImportSource @emotion/react */
import { useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { PanelCard } from '../../../design-system/molecules/PanelCard/PanelCard';
import { ChartLegend } from '../../../design-system/molecules/ChartLegend/ChartLegend';
import { theme } from '../../../design-system/theme/theme';
import type { RunState } from '../../types';
import { splitByCollector } from '../../utils/moduleOrder';
import {
  titleCaptionStyle,
  heatWrapStyle,
  rowStyle,
  nameStyle,
  nameClickableStyle,
  cellsStyle,
  csvGroupStyle,
  cellStyle,
  hintStyle,
  stateColor,
  tooltipStyle,
  tooltipTitleStyle,
  tooltipRowStyle,
  tooltipDotStyle,
  tooltipHintStyle,
} from './RunHeatmap.styles';

export interface RunHeatmapRow {
  name: string;
  /** 최근 실행 상태 문자열. 예: "SSSFSR__" */
  runs: string;
  /** 확인 필요 모듈 여부 — 이름을 danger 색으로 강조. */
  bad?: boolean;
  /** 각 셀의 날짜/시간. hover 시 표시 */
  dates?: string[];
  /** 각 셀의 Job ID. 클릭 시 상세 이동용. 미실행/패딩 셀은 null. runs와 인덱스 정렬. */
  ids?: (number | null)[];
  /** 가장 최근 실행의 collectorType. csv parser 여부로 정렬·그룹 구분에 사용. */
  collectorType?: string;
}

export interface RunHeatmapProps {
  rows: RunHeatmapRow[];
  /** 제목 옆 보조 문구. 예: "최근 20회 · 모듈별" (선택) */
  subLabel?: ReactNode;
  /** 셀 클릭 → 해당 Job 상세로 이동. S/F/R 이면서 id가 있는 셀만 클릭 가능. */
  onCellClick?: (jobId: number) => void;
  /** 모듈명 클릭 → 해당 모듈로 필터 건 수집 이력으로 이동. */
  onModuleClick?: (moduleName: string) => void;
  /** 하단 안내 문구. (선택) */
  hint?: ReactNode;
}

const CLICKABLE: RunState[] = ['S', 'Y', 'F', 'R'];

/** 상태 문자 → 한글 라벨. 툴팁 표시용. */
const STATE_LABEL: Record<RunState, string> = {
  S: '성공',
  Y: '재시도 성공',
  F: '실패',
  R: '진행 중',
  U: '대기',
  _: '기록 없음',
};

interface TipState {
  x: number;
  y: number;
  content: ReactNode;
}

/** 실행 히트맵. 행=모듈, 열=최근 실행 슬롯 — 수십 모듈 상태를 한 화면에 표시. */
export function RunHeatmap({ rows, subLabel, onCellClick, onModuleClick, hint }: RunHeatmapProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<TipState | null>(null);

  // 셀 hover 시 커서 위에 즉시 툴팁 표시. x는 컨테이너 폭 안으로 클램프.
  const showTip = (content: ReactNode) => (e: MouseEvent) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.min(Math.max(e.clientX - rect.left, 4), rect.width - 4);
    setTip({ x, y: e.clientY - rect.top, content });
  };
  const hideTip = () => setTip(null);

  // 한 모듈 행 렌더. withGap이면 앞 그룹과 여백(csv parser 그룹의 첫 행).
  const renderRow = (row: RunHeatmapRow, withGap = false) => (
    <div key={row.name} css={[rowStyle, withGap && csvGroupStyle]}>
      <div
        css={[nameStyle(row.bad), onModuleClick && nameClickableStyle]}
        onClick={onModuleClick ? () => onModuleClick(row.name) : undefined}
        title={onModuleClick ? `${row.name} 수집 이력 보기` : undefined}
      >
        {row.name}
      </div>
      <div css={cellsStyle}>
        {[...row.runs].map((c, i) => {
          const state = c as RunState;
          const jobId = row.ids?.[i] ?? null;
          const clickable =
            onCellClick !== undefined && CLICKABLE.includes(state) && jobId !== null;
          const dateStr = row.dates?.[i] || '';
          // 툴팁: 제목=날짜/시각(없으면 모듈명), 본문=상태 색 점+라벨, 클릭 가능 시 안내
          const content = (
            <>
              <span css={tooltipTitleStyle}>{dateStr || row.name}</span>
              <div css={tooltipRowStyle}>
                <span css={tooltipDotStyle(stateColor[state])} />
                {STATE_LABEL[state] ?? state}
              </div>
              {clickable && <span css={tooltipHintStyle}>클릭 → 상세 보기</span>}
            </>
          );
          return (
            <span
              key={i}
              css={cellStyle(state, clickable)}
              onMouseMove={showTip(content)}
              onMouseLeave={hideTip}
              onClick={clickable ? () => onCellClick(jobId!) : undefined}
            />
          );
        })}
      </div>
    </div>
  );

  // 가장 최근 실행의 collectorType이 csv parser가 아닌 모듈을 먼저,
  // csv parser 모듈은 아래에 여백을 두고 나열. (편성표와 공유하는 공용 규칙)
  const { nonCsv: nonCsvRows, csv: csvRows } = splitByCollector(rows);

  return (
    <PanelCard
      collapsible
      title={
        <>
          실행 히트맵 {subLabel !== undefined && <span css={titleCaptionStyle}>{subLabel}</span>}
        </>
      }
      headerRight={
        <ChartLegend
          items={[
            { color: theme.color.success, label: '성공' },
            { color: theme.color.success2, label: '재시도 성공' },
            { color: theme.color.danger, label: '실패' },
            { color: theme.color.running, label: '진행' },
            { color: theme.color.border, label: '누락/미실행' },
          ]}
        />
      }
    >
      <div css={heatWrapStyle} ref={wrapRef}>
        {nonCsvRows.map((row) => renderRow(row))}
        {/* csv parser 그룹: 첫 행에만 여백(앞 그룹이 있을 때) */}
        {csvRows.map((row, idx) => renderRow(row, idx === 0 && nonCsvRows.length > 0))}
        {tip && (
          <div css={tooltipStyle} style={{ left: tip.x, top: tip.y }}>
            {tip.content}
          </div>
        )}
      </div>
      {hint !== undefined && <p css={hintStyle}>{hint}</p>}
    </PanelCard>
  );
}
